/**
 * src/components/CommercePolicyPanel.js
 *
 * REFUND AND CANCELLATION INFORMATION, RESOLVED FOR THE CUSTOMER'S JURISDICTION.
 *
 * The owner requires that refund and cancellation information shows based on the customer's
 * country, and that it is visible where it is useful rather than buried in a global Terms page.
 *
 * Design rules this component follows:
 *   - CONCISE FIRST. One short summary line per subscription; the full applicable terms open on
 *     request. Legal prose does not flood the screen.
 *   - THE SERVER DECIDES. Every sentence here comes from GET /v1/commerce-policy/*, which resolves
 *     the customer's jurisdiction server-side. This file computes no policy and holds no country
 *     logic of its own.
 *   - NEVER DECEPTIVE. VISINVOKE's own voluntary policy and statutory consumer rights are shown as
 *     two separate things, and statutory rights are always stated.
 *   - CANCELLING IS NOT HARDER THAN BUYING. One button, one confirmation that states exactly what
 *     will happen, and the real period-end date.
 *
 * It renders inside the existing dark VISINVOKE surface and introduces no layout of its own beyond
 * a bordered panel.
 */
import React, { useCallback, useEffect, useState } from 'react';
import API from '../api';

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

export function useCommercePolicy() {
  const [state, setState] = useState({ loading: true, error: '', subscriptions: [], jurisdiction: null });

  const load = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true, error: '' }));
    Promise.all([
      API.get('/v1/commerce-policy/subscriptions'),
      API.get('/v1/commerce-policy/summary'),
    ])
      .then(([subsRes, summaryRes]) => {
        setState({
          loading: false,
          error: '',
          subscriptions: (subsRes.data && subsRes.data.subscriptions) || [],
          jurisdiction: (summaryRes.data && summaryRes.data.jurisdiction) || null,
        });
      })
      .catch(() => {
        setState({
          loading: false,
          error: 'Refund and cancellation information could not be loaded right now.',
          subscriptions: [],
          jurisdiction: null,
        });
      });
  }, []);

  useEffect(load, [load]);
  return { ...state, reload: load };
}

const PRODUCT_LABELS = {
  subscription: 'VISINVOKE Plan',
  service_command_centre: 'Service Command Centre',
  combat_pro_cloud: 'Combat PRO Cloud',
  standalone_generators: 'Standalone Generators',
  content_planner: 'Content Planner',
};

// A product name on its own does not tell a customer what they are paying for, and "VISINVOKE Plan"
// is the worst of them: it is the same three words whichever plan you hold. Each line below says
// what the subscription covers, in the customer's terms, and nothing more than the entitlement it
// actually grants.
const PRODUCT_COVERAGE = {
  subscription: 'Your image generation plan: the monthly spec allowance used by the D&D and Professional image generators.',
  service_command_centre: 'Service Command Centre: the job, client, device and operator management system for a service business.',
  combat_pro_cloud: 'Cloud sync for Combat PRO: your encounters and combat data kept in sync across devices.',
  standalone_generators: 'Standalone generators: the RPG generators you run in the Desktop Command Centre, billed separately from any image plan.',
  content_planner: 'Content Planner: the planning and scheduling workspace.',
};

// plan_label is whatever each entitlement authority calls the thing the customer bought: a plan
// name for the image plan, a tier for Combat PRO Cloud, an access mode for standalone generators.
// The access modes are internal words, so they are said in plain English here.
const ACCESS_MODE_LABELS = {
  pack: 'Generator pack',
  individual: 'Individual selection',
};

function planDetail(entry) {
  const raw = entry.plan_label;
  if (!raw || typeof raw !== 'string') return null;
  if (entry.product === 'standalone_generators') {
    return ACCESS_MODE_LABELS[raw.toLowerCase()] || raw;
  }
  const label = PRODUCT_LABELS[entry.product];
  // Service Command Centre labels itself; repeating the name after the name reads as a stutter.
  if (label && raw.toLowerCase() === label.toLowerCase()) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'long', year: 'numeric' });
}

// A stored period end that has already passed is not a next billing date, and calling it one is a
// false statement to a paying customer. It happens when a renewal did not reach the record behind
// this row, so the honest thing is to say the date is not current and point at the billing history.
function isPast(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

// ---------------------------------------------------------------------------
// One subscription row
// ---------------------------------------------------------------------------

function SubscriptionPolicyRow({ entry, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const policy = entry.policy || {};
  const copy = policy.customer_copy || {};
  const label = PRODUCT_LABELS[entry.product] || entry.product;
  const detail = planDetail(entry);
  const coverage = PRODUCT_COVERAGE[entry.product] || null;
  const periodEnd = formatDate(entry.current_period_end);
  const periodEndPassed = isPast(entry.current_period_end);

  const handleCancel = async () => {
    const confirmText = periodEnd
      ? `Cancel ${label}?\n\nYour access continues until ${periodEnd}. You will not be charged again. No refund is issued by cancelling.`
      : `Cancel ${label}?\n\nYour access continues until the end of the period you have already paid for. You will not be charged again. No refund is issued by cancelling.`;
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await API.post(`/v1/commerce-policy/subscriptions/${entry.product}/cancel`);
      setMessage((res.data && res.data.message) || 'Cancelled. You will not be charged again.');
      if (onChanged) onChanged();
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.message)
        || 'The cancellation could not be completed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleResume = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await API.post(`/v1/commerce-policy/subscriptions/${entry.product}/resume`);
      setMessage('Your subscription will renew as normal.');
      if (onChanged) onChanged();
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.message)
        || 'The subscription could not be resumed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={cp.row}>
      <div style={cp.rowHead}>
        <div style={{ minWidth: 0 }}>
          <div style={cp.rowTitle}>
            {label}
            {detail ? <span style={cp.rowPlan}>{detail}</span> : null}
          </div>
          {coverage ? <div style={cp.rowCoverage}>{coverage}</div> : null}
          <div style={cp.rowMeta}>
            {entry.cancel_at_period_end ? (
              <span style={cp.pillCancelled}>
                Cancelled{periodEnd ? ` · access continues until ${periodEnd}` : ''}
              </span>
            ) : (
              <span style={periodEndPassed ? cp.pillStale : cp.pillActive}>
                {periodEnd
                  ? (periodEndPassed ? `Renewal date not current · last recorded ${periodEnd}` : `Next billing date ${periodEnd}`)
                  : 'Active'}
              </span>
            )}
            <span style={cp.rowCharge}>
              {entry.will_charge_again ? 'You will be charged again' : 'You will not be charged again'}
            </span>
          </div>
        </div>
        <div style={cp.rowActions}>
          {entry.cancel_at_period_end ? (
            <button style={{ ...cp.btnSecondary, opacity: busy ? 0.6 : 1 }} onClick={handleResume} disabled={busy}>
              {busy ? 'Working...' : 'Resume'}
            </button>
          ) : (
            <button style={{ ...cp.btnCancel, opacity: busy ? 0.6 : 1 }} onClick={handleCancel} disabled={busy}>
              {busy ? 'Working...' : 'Cancel'}
            </button>
          )}
        </div>
      </div>

      {periodEndPassed && !entry.cancel_at_period_end ? (
        <p style={cp.staleNote}>
          Your subscription is active and this date has passed, so it is not the date of your next
          payment. Your Billing History shows what has actually been charged. Contact support if the
          two do not agree.
        </p>
      ) : null}

      {/* Concise summary. The full applicable terms are one click away, not on screen by default. */}
      {copy.cancellation ? <p style={cp.summary}>{copy.cancellation}</p> : null}

      {message ? <div style={cp.ok}>{message}</div> : null}
      {error ? <div style={cp.err}>{error}</div> : null}

      <button style={cp.linkBtn} onClick={() => setExpanded(!expanded)}>
        {expanded ? 'Hide refund and cancellation terms' : 'Refund and cancellation terms'}
      </button>

      {expanded ? (
        <div style={cp.detail}>
          <div style={cp.detailBlock}>
            <div style={cp.detailLabel}>VISINVOKE policy</div>
            <div style={cp.detailText}>{copy.voluntary}</div>
          </div>
          <div style={cp.detailBlock}>
            <div style={cp.detailLabel}>Your consumer rights</div>
            <div style={cp.detailText}>{copy.statutory}</div>
            <div style={cp.detailText}>{copy.statutory_remedies}</div>
          </div>
          <div style={cp.detailFoot}>
            {policy.country ? `Resolved for ${policy.country}${policy.subdivision ? '-' + policy.subdivision : ''}. ` : ''}
            {policy.regime_name ? `${policy.regime_name}. ` : ''}
            {policy.voluntary_policy_version ? `Policy version ${policy.voluntary_policy_version}.` : ''}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export default function CommercePolicyPanel() {
  const { loading, error, subscriptions, jurisdiction, reload } = useCommercePolicy();

  if (loading) {
    return (
      <div style={cp.section}>
        <h3 style={cp.title}>Subscriptions, Refunds &amp; Cancellation</h3>
        <p style={cp.muted}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={cp.section}>
      <h3 style={cp.title}>Subscriptions, Refunds &amp; Cancellation</h3>

      {error ? <div style={cp.err}>{error}</div> : null}

      {jurisdiction && jurisdiction.needs_customer_confirmation ? (
        <div style={cp.notice}>
          We could not confirm which country&rsquo;s consumer law applies to your account, so the
          refund and cancellation rights shown here may be incomplete. Add your country in Account
          Details, or contact support and your purchase will be reviewed individually. This does not
          reduce any rights you have.
        </div>
      ) : null}

      {subscriptions.length === 0 ? (
        <p style={cp.muted}>You have no active subscriptions.</p>
      ) : (
        subscriptions.map((entry) => (
          <SubscriptionPolicyRow key={entry.product} entry={entry} onChanged={reload} />
        ))
      )}

      <p style={cp.footnote}>
        Cancelling stops the next payment. It does not end your access early and does not issue a
        refund. Your rights under the consumer law where you live apply in addition to this policy.
      </p>
    </div>
  );
}

const cp = {
  section: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 14,
    padding: '22px 24px',
    marginBottom: 24,
  },
  title: { margin: '0 0 14px', fontSize: 17, fontWeight: 800, color: '#e8eefc', letterSpacing: 0.2 },
  muted: { margin: 0, fontSize: 14, color: '#9ca3af' },
  notice: {
    background: 'rgba(251,191,36,0.10)',
    border: '1px solid rgba(251,191,36,0.40)',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    lineHeight: 1.55,
    color: '#fde68a',
    marginBottom: 16,
  },
  row: {
    borderTop: '1px solid rgba(255,255,255,0.08)',
    paddingTop: 16,
    marginTop: 16,
  },
  rowHead: { display: 'flex', alignItems: 'flex-start', gap: 16, justifyContent: 'space-between' },
  rowTitle: { fontSize: 15, fontWeight: 700, color: '#e8eefc' },
  rowPlan: { fontSize: 13, fontWeight: 600, color: '#a5b4fc', marginLeft: 8 },
  rowCoverage: { fontSize: 12.5, lineHeight: 1.5, color: '#9ca3af', marginTop: 4 },
  rowMeta: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 6 },
  rowCharge: { fontSize: 12.5, color: '#9ca3af' },
  rowActions: { flexShrink: 0 },
  pillActive: {
    fontSize: 12, fontWeight: 700, color: '#34d399',
    border: '1px solid rgba(52,211,153,0.45)', background: 'rgba(52,211,153,0.12)',
    borderRadius: 999, padding: '3px 11px',
  },
  pillStale: {
    fontSize: 12, fontWeight: 700, color: '#fbbf24',
    border: '1px solid rgba(251,191,36,0.45)', background: 'rgba(251,191,36,0.12)',
    borderRadius: 999, padding: '3px 11px',
  },
  staleNote: { fontSize: 12.5, lineHeight: 1.6, color: '#fcd34d', margin: '10px 0 0' },
  pillCancelled: {
    fontSize: 12, fontWeight: 700, color: '#fbbf24',
    border: '1px solid rgba(251,191,36,0.45)', background: 'rgba(251,191,36,0.12)',
    borderRadius: 999, padding: '3px 11px',
  },
  summary: { margin: '10px 0 0', fontSize: 13.5, lineHeight: 1.6, color: '#cbd5e1' },
  linkBtn: {
    background: 'none', border: 'none', padding: '8px 0 0', cursor: 'pointer',
    color: '#78ADFF', fontSize: 13, fontWeight: 700, textDecoration: 'underline',
  },
  btnCancel: {
    background: 'transparent', color: '#fca5a5',
    border: '1px solid rgba(248,113,113,0.55)', borderRadius: 999,
    padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
  },
  btnSecondary: {
    background: 'transparent', color: '#78ADFF',
    border: '1px solid rgba(120,173,255,0.55)', borderRadius: 999,
    padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
  },
  detail: {
    marginTop: 12, padding: '14px 16px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
  },
  detailBlock: { marginBottom: 12 },
  detailLabel: { fontSize: 11.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: '#9ca3af', marginBottom: 5 },
  detailText: { fontSize: 13, lineHeight: 1.6, color: '#cbd5e1', marginBottom: 6 },
  detailFoot: { fontSize: 11.5, color: '#6b7280', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 8 },
  ok: { marginTop: 10, fontSize: 13, color: '#34d399' },
  err: { marginTop: 10, fontSize: 13, color: '#fca5a5' },
  footnote: { marginTop: 18, marginBottom: 0, fontSize: 12, lineHeight: 1.6, color: '#6b7280' },
};
